import { useMemo } from 'react';
import { useSamplePreviewContext } from '@/document-types/application/hooks/useSamplePreviewContext';
import { usePreviewScenarioContext } from '@/document-types/application/hooks/usePreviewScenarioContext';
import type { DocumentTypeRef } from '@/document-types/domain/types/DocumentType';
import type { DocumentContext } from '@/document-render/domain/DocumentContext';
import { normalizeDocumentContext } from '@/document-render/application/normalizeDocumentContext';
import { getMockDocumentContext } from '@/document-render/mocks/MOCK_DOCUMENT_CONTEXT';
import { usePreviewContextHost, type PreviewContextSource } from '@/document-render/presentation/PreviewContextProvider';

const asSafeContext = (ctx: DocumentContext): DocumentContext => normalizeDocumentContext(ctx as unknown as Record<string, unknown>);

/** Mark sample/fallback bags so cover shows token placeholders instead of mock filled values. */
const asUnboundSampleContext = (ctx: DocumentContext): DocumentContext => ({
  ...ctx,
  previewValueBinding: 'unbound'
});

export type PreviewContextState = {
  context: DocumentContext;
  isLoading: boolean;
  error: unknown;
  source: PreviewContextSource;
};

export const usePreviewContext = (ref?: DocumentTypeRef, selectedScenarioKey?: string | null): PreviewContextState => {
  const { hostContext, resolveContext } = usePreviewContextHost();
  const {
    data: scenarioContext,
    isLoading: isScenarioLoading,
    isError: isScenarioError,
    error: scenarioError,
    isFetched: isScenarioFetched
  } = usePreviewScenarioContext(ref, selectedScenarioKey);
  const { data: apiContext, isLoading, isError, error, isFetched } = useSamplePreviewContext(ref);

  return useMemo(() => {
    const resolvedHost = ref ? resolveContext?.(ref) : undefined;
    if (resolvedHost) {
      return { context: asSafeContext(resolvedHost), isLoading: false, error: null, source: 'host' as const };
    }

    if (hostContext) {
      return { context: asSafeContext(hostContext), isLoading: false, error: null, source: 'host' as const };
    }

    if (selectedScenarioKey) {
      if (scenarioContext) {
        return { context: scenarioContext, isLoading: false, error: null, source: 'scenario' as const };
      }

      if (isScenarioLoading && !isScenarioFetched) {
        return {
          context: asUnboundSampleContext(getMockDocumentContext()),
          isLoading: true,
          error: null,
          source: 'scenario' as const
        };
      }

      if (isScenarioError) {
        return {
          context: asUnboundSampleContext(getMockDocumentContext()),
          isLoading: false,
          error: scenarioError,
          source: 'scenario' as const
        };
      }
    }

    if (apiContext) {
      return {
        context: asUnboundSampleContext(apiContext),
        isLoading: false,
        error: null,
        source: 'api' as const
      };
    }

    if (ref && isLoading && !isFetched) {
      return {
        context: asUnboundSampleContext(getMockDocumentContext()),
        isLoading: true,
        error: null,
        source: 'fallback' as const
      };
    }

    if (ref && isError) {
      if (import.meta.env.DEV) {
        console.warn('[usePreviewContext] Using defensive fallback preview context. Sample context API failed.', {
          ref,
          error
        });
      }

      return {
        context: asUnboundSampleContext(getMockDocumentContext()),
        isLoading: false,
        error,
        source: 'fallback' as const
      };
    }

    return {
      context: asUnboundSampleContext(getMockDocumentContext()),
      isLoading: false,
      error: null,
      source: 'fallback' as const
    };
  }, [
    apiContext,
    error,
    hostContext,
    isError,
    isFetched,
    isLoading,
    isScenarioError,
    isScenarioFetched,
    isScenarioLoading,
    ref,
    resolveContext,
    scenarioContext,
    scenarioError,
    selectedScenarioKey
  ]);
};
